import { Component, OnDestroy, OnInit } from '@angular/core';
import { DbService } from '../../core/db/db.service';
import moment from 'moment';
import { AuthService } from '../../auth/auth.service';
import { LoggerService } from '../../core/logger/logger.service';
import { StatisticsData } from './statistics-data.interface';
import { UtilsService } from '../../core/utils/utils.service';
import { Project } from '../../projects/project.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-activity-statistics',
    templateUrl: './activity-statistics.component.html',
    styleUrls: ['./activity-statistics.component.scss'],
})
export class ActivityStatisticsComponent implements OnInit, OnDestroy {
    todaysOverallJobCount = 0;
    yesterdaysOverallJobCount = 0;
    changeFactor = 0;
    changeFactorDirection: 'positive' | 'negative' = null;

    todaysProjectJobCount: Array<{
        projectId: string;
        projectName: string;
        projectWorkers: string;
        count: number;
    }> = [];

    loading = true;
    loadedOnce = false;
    offlineError = false;
    noDataError = false;
    updateTimestamp = 0;
    updateIntervalId = null;

    constructor(
        private db: DbService,
        private authSrv: AuthService,
        private logger: LoggerService,
        private utils: UtilsService,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.loadData();

        // set interval to refresh data every 2 minutes
        this.updateIntervalId = setInterval(() => {
            this.loadData();
        }, 120000);
    }

    ngOnDestroy() {
        if (this.updateIntervalId != null) {
            clearInterval(this.updateIntervalId);
        }
    }

    /**
     * Loads current data from dynamodb
     */
    async loadData() {
        if (!this.loadedOnce) {
            // only display loading indicator if we do not already have old data (so on data refreshments)
            this.loading = true;
            this.offlineError = false;
            this.noDataError = false;
        }

        if (!this.utils.isOnline) {
            this.offlineError = true;
            this.loading = false;
            return undefined;
        }

        this.logger.log('loading activity statistics from dynamodb now');

        let data = await this.db.dynamoDb
            .query({
                TableName: 'dp_stat_count',
                IndexName: 'sk-pk-index',
                KeyConditionExpression: '#pKeyName = :pKeyValue AND begins_with(#sKeyName, :sKeyValue)',
                ExpressionAttributeNames: {
                    '#pKeyName': 'sk',
                    '#sKeyName': 'pk',
                },
                ExpressionAttributeValues: {
                    ':pKeyValue': moment().format('YYYY.MM.DD'),
                    ':sKeyValue': this.authSrv.authInfo.companyData.pk,
                },
            })
            .promise();
        let items = data.Items as Array<StatisticsData>;

        if (data.Items.length == 0) {
            this.noDataError = true;
            this.loading = false;
            return undefined;
        }

        this.todaysProjectJobCount = [];
        let tempProjectJobCount = [];

        for (let item of items) {
            let pkSplit = item.pk.split('#'); // split on # character
            if (pkSplit[2] === 'job') {
                // if the pk is formatted like company#companyId#job then this item is the total company job count for this day
                this.todaysOverallJobCount = item.count;
            } else if (pkSplit[2] === 'project' && pkSplit[4] == 'job') {
                // if the pk is formatted like company#companyId#project#projectId#job then this item is job count for given project id for this day
                // get the project id from the splitted pk

                // build workers string
                let workerString = `${this.translate.instant('dashboard.status.activity.from')} `;
                if (item.projectWorkers != null) {
                    let workerCount = item.projectWorkers.values.length;
                    if (workerCount > 3) {
                        // only display the first 3 workers and a notice that there are more
                        workerString += item.projectWorkers.values.slice(0, 4).join(', ');
                        workerString += ` ${this.translate.instant('dashboard.status.activity.and')} ${item.projectWorkers.values.length - 3} ${this.translate.instant('dashboard.status.activity.more')}`;
                    } else {
                        workerString += item.projectWorkers.values.join(', ');
                    }
                }

                tempProjectJobCount.push({
                    projectId: pkSplit[3],
                    projectName: null,
                    projectWorkers: workerString,
                    count: item.count,
                });
            }
        }

        // loop through projects and get them from db as we want to know the project name (and whether the project is available locally at all)
        for (let project of tempProjectJobCount) {
            try {
                let projectData = (await this.db.getItemById(project.projectId).toPromise()) as Project;
                project.projectName = projectData.name;
                this.todaysProjectJobCount.push(project);
            } catch (dbErr) {
                // do nothing, looks like the project is not available locally, so we will not show any information about this project
            }
        }

        // make another call to dynamodb to get the total job count of yesterday (so we can display percentual change to yesterday)
        let yesterdaysJobCountData = await this.db.dynamoDb
            .query({
                TableName: 'dp_stat_count',
                IndexName: 'sk-pk-index',
                KeyConditionExpression: '#pKeyName = :pKeyValue AND #sKeyName = :sKeyValue',
                ExpressionAttributeNames: {
                    '#pKeyName': 'sk',
                    '#sKeyName': 'pk',
                },
                ExpressionAttributeValues: {
                    ':pKeyValue': moment().subtract(1, 'days').format('YYYY.MM.DD'),
                    ':sKeyValue': this.authSrv.authInfo.companyData.pk + '#job',
                },
                Limit: 1,
            })
            .promise();
        if (yesterdaysJobCountData.Items.length == 0) {
            // no data found, display 0
            this.yesterdaysOverallJobCount = 0;
        } else {
            this.yesterdaysOverallJobCount = (yesterdaysJobCountData.Items[0] as StatisticsData).count;
        }

        // calculate change factor
        if (this.yesterdaysOverallJobCount > 0) {
            this.changeFactor = Math.round(
                ((this.todaysOverallJobCount - this.yesterdaysOverallJobCount) / this.yesterdaysOverallJobCount) * 100
            );
            if (this.changeFactor > 0) {
                this.changeFactorDirection = 'positive';
            } else {
                this.changeFactorDirection = 'negative';
                this.changeFactor = Math.round(Math.abs(this.changeFactor));
            }
        } else {
            this.changeFactor = 0;
        }

        this.updateTimestamp = Date.now();

        this.loading = false;
        this.loadedOnce = true;
        return true;
    }
}
